import { EventEmitter } from '@angular/core';
import { assign, pick } from 'lodash';

export type StepByStepWizardStepCheckFnType<ContextType> = (context: ContextType) => boolean;

export class StepByStepWizardStep<ContextType> {
  public id: string;
  public title: string;
  public checks: Array<boolean | StepByStepWizardStepCheckFnType<ContextType>>;
  public context: ContextType;

  protected _parent: StepByStepWizardSteps<ContextType>;
  protected _complete = false;
  protected _active = false;
  protected _visible = false;

  public constructor(values?: Omit<StepByStepWizardStep<ContextType>, 'done' | 'setParent' | 'complete'>) {
    if (values) {
      assign(this, values);
    }
  }

  public setParent(value: StepByStepWizardSteps<ContextType>) {
    this._parent = value;
  }

  public set complete(value: boolean) {
    this._complete = value;
    this._parent?.changed$.emit(true);
  }

  public get complete(): boolean {
    return this._complete;
  }

  public set active(value: boolean) {
    this._active = value;
    this._parent?.changed$.emit(true);
  }

  public get active(): boolean {
    return this._active;
  }

  public set visible(value: boolean) {
    this._visible = value;
    this._parent?.changed$.emit(true);
  }

  public get visible(): boolean {
    return this._visible;
  }

  public get done(): boolean {
    const checks = this.checks || [];
    if (checks.length === 0) {
      return true;
    }
    return checks.every((check) => {
      if (check === true) {
        return true;
      }
      if (check === false) {
        return false;
      }
      return check(this.context);
    });
  }
}

export class StepByStepWizardSteps<ContextType> {
  public changed$ = new EventEmitter<boolean>();
  public activeStep$ = new EventEmitter<string>();
  public activeStep: string;

  public constructor(public steps: Array<StepByStepWizardStep<ContextType>>) {
    this.steps.map((step) => step.setParent(this));
    const activeStep = this.steps.find((s) => s.active);
    if (activeStep) {
      this.goStep(activeStep.id);
    }
  }

  public addStep(step: StepByStepWizardStep<ContextType>) {
    step.setParent(this);
    this.steps.push(step);
    if (step.active) {
      this.goStep(step.id);
    }
  }

  public get dump() {
    return {
      activeStep: this.activeStep || null,
      steps: this.steps.map((s) => pick(s, ['id', 'title', 'active', 'complete', 'visible'])),
    };
  }

  public get(id: string): StepByStepWizardStep<ContextType> {
    return this.steps.find((step) => step.id === id);
  }

  public goNextStep() {
    const activeIndex = this.steps.findIndex((step) => step.visible && step.active);
    const nextStepIndex = this.steps.findIndex((step, index) => step.visible && index > activeIndex);
    if (nextStepIndex >= 0) {
      const nextStep = this.steps[nextStepIndex];
      this.goStep(nextStep.id);
    }
  }

  public goStep(id: string) {
    const nextStep = this.get(id);
    if (nextStep && nextStep.visible) {
      this.deactivateAllSteps();
      nextStep.active = true;
      this.activeStep = nextStep.id;
      this.activeStep$.next(this.activeStep);
    }
  }

  protected deactivateAllSteps() {
    this.steps.filter((step) => step.active).map((step) => (step.active = false));
  }
}
